import { React, useState } from 'libs/react';
import { classNames, useDebounce, useQuery } from 'libs/react';
import { gql } from '@apollo/client';
import { FormInput } from 'components/form/FormInput';
import { Waiter } from 'components/Waiter';
import { formatDateOnly } from 'helpers/formatDateOnly';
import './DealSearchList.scss';
/**
 * Lightweight deal list for the picker; the search itself (number, title,
 * client, contact, partner) runs server-side in the `deals` query.
 * @const DealSearchListQuery
 * @since 1.0.0
 */
const DealSearchListQuery = gql `
  query DealSearchListQuery($input: DealsInput) {
    deals(input: $input) {
      items {
        id
        number
        title
        status
        plannedDateStart
        plannedDateEnd

        currency {
          id
          code
        }

        client {
          name
        }

        contact {
          fullname
        }

        partner {
          name
        }
      }
    }
  }
`;
/**
 * @const RESULT_LIMIT
 * @since 1.0.0
 */
const RESULT_LIMIT = 20;
/**
 * Searchable list of deals to pick one from.
 *
 * @component DealSearchList
 * @since 1.0.0
 */
export function DealSearchList(props) {
    var _a, _b;
    const { selected, onSelect, onConfirm, excludeId, disabled } = props;
    const [inputValue, setInputValue] = useState('');
    const [search, setSearch] = useState('');
    const setSearchDebounced = useDebounce(setSearch, 400);
    const [data, ready] = useQuery(DealSearchListQuery, {
        variables: {
            input: {
                search: search.trim(),
                limit: RESULT_LIMIT,
                order: 'date:DESC',
                directSale: false
            }
        },
        fetchPolicy: 'network-only'
    });
    const deals = ((_b = (_a = data === null || data === void 0 ? void 0 : data.deals) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []).filter((deal) => deal.id !== excludeId);
    /**
     * @function onSearchChange
     * @since 1.0.0
     * @hidden
     */
    function onSearchChange(value) {
        setInputValue(value);
        setSearchDebounced(value);
    }
    return (React.createElement("div", { className: classNames('deal-search-list', props.className) },
        React.createElement(FormInput, { name: "search", label: t('Deal'), picto: "magnifying-glass", placeholder: t('Search by number, title, client, contact...'), value: inputValue, onChange: onSearchChange }),
        selected && (React.createElement("div", { className: "deal-search-list-selected" },
            t('Selected'),
            " : ",
            getDealTitle(selected))),
        React.createElement("div", { className: "deal-search-list-results" },
            React.createElement(Waiter, { until: ready },
                deals.length === 0 && (React.createElement("div", { className: "deal-search-list-empty" }, t('No deals found'))),
                deals.map((deal) => (React.createElement("div", { key: deal.id, className: classNames('deal-search-list-row', {
                        'deal-search-list-row--selected': (selected === null || selected === void 0 ? void 0 : selected.id) === deal.id
                    }), onClick: () => onSelect(deal), onDoubleClick: () => !disabled && (onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm(deal)) },
                    React.createElement("div", { className: "deal-search-list-row-main" },
                        React.createElement("div", { className: "deal-search-list-row-title" }, getDealTitle(deal)),
                        React.createElement("div", { className: "deal-search-list-row-infos" }, getDealInfos(deal))),
                    React.createElement("div", { className: "deal-search-list-row-status" }, getStatusLabel(deal.status)))))))));
}
/**
 * @function getDealTitle
 * @since 1.0.0
 * @hidden
 */
function getDealTitle(deal) {
    return [deal.number && `#${deal.number}`, deal.title]
        .filter(Boolean)
        .join(' — ');
}
/**
 * @function getDealInfos
 * @since 1.0.0
 * @hidden
 */
function getDealInfos(deal) {
    var _a, _b, _c;
    let plannedDate = '';
    if (deal.plannedDateStart) {
        plannedDate = formatDateOnly(deal.plannedDateStart);
        if (deal.plannedDateEnd && deal.plannedDateEnd !== deal.plannedDateStart) {
            plannedDate += ' → ' + formatDateOnly(deal.plannedDateEnd);
        }
    }
    return [
        (_a = deal.client) === null || _a === void 0 ? void 0 : _a.name,
        (_b = deal.contact) === null || _b === void 0 ? void 0 : _b.fullname,
        (_c = deal.partner) === null || _c === void 0 ? void 0 : _c.name,
        plannedDate
    ]
        .filter(Boolean)
        .join(' · ');
}
/**
 * @function getStatusLabel
 * @since 1.0.0
 * @hidden
 * Literal calls so the i18n extractor picks the strings up.
 */
function getStatusLabel(status) {
    switch (status) {
        case 'WON':
            return t('Won');
        case 'LOST':
            return t('Rejected');
        case 'CANCELLED':
            return t('Cancelled');
        case 'INACTIVE':
            return t('Inactive');
        default:
            return t('Active');
    }
}
